import * as Sentry from "@sentry/react";
import { Fragment } from "react";
import { CssBaseline, CircularProgress, GlobalStyles, Box } from "@mui/material";
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, ScrollRestoration } from "react-router";
import { RouterProvider } from "react-router/dom";
import { Jimo } from "./components/ExternalComponents/Jimo/Jimo.tsx";
import { BaseTemplate } from "./pages/BaseLayout/BaseTemplate.tsx";
import { useAuthRouter, useNoCompanyRouter } from "./pages/Authentication/AuthRouter";
import { GenericError } from "./components/GenericError.tsx";
import { t } from "i18next";
import { ProtectedContextProvider } from "src/context/ProtectedContext";

import { useApplicationParameters } from "./api/client-api/appParams/useApplicationParameters.ts";
import { HomeSwitch } from "./pages/HomeSwitch/HomeSwitch.tsx";
import { LocalStorage } from "./services/LocalStorage.ts";
import Documentation from "./pages/Documentation/Documentation.tsx";
import CMSPages from "./pages/CMS/CMSPages";

import { UserProvider } from "./context/User/provider.tsx";
import { DebugProvider } from "./context/Debug/DebugContext";
import { ThemedApp } from "./ThemedApp.tsx";
import { PluginProvider } from "./plugins/core/PluginProvider.tsx";
import type { PluginConfig } from "./plugins/core/types";

import { useCompanyRouter } from "./pages/Company/companyRouter";
import { useSettingsRouter } from "./pages/Settings/useSettingsRouter";
import { useCMSRouter } from "./pages/CMS/useCMSRouter";
import { usePagesRouter } from "./pages/CMS/usePagesRouter";
import { useSocialRouter } from "./pages/Social/socialRouter";
import { useStrategyRouter } from "./pages/Strategy/strategyRouter";
import { useCompanySettingsRouter } from "./pages/CompanySettings/companySettingsRouter";
import { NetworkPage } from "./pages/Network/NetworkPage";
import { companySlug, networkSlug } from "./services/URLHandler";
import { useProfileSettingsRouter } from "./pages/ProfileSettings/profileSettingsRouter";
import { isAppInPDFMode } from "src/utils/rendering";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

const App = () => {
    // https://github.com/remix-run/react-router/discussions/10160
    // This thread explains why we use the form of a react hook.
    // This message specifically :
    // https://github.com/remix-run/react-router/discussions/10160#discussioncomment-5958171
    const authRouter = useAuthRouter();
    const noCompanyRouter = useNoCompanyRouter();
    const companyRouter = useCompanyRouter();
    const pagesRouter = usePagesRouter();
    const cmsRouter = useCMSRouter();
    const settingsRouter = useSettingsRouter();
    const strategyRouter = useStrategyRouter();
    const socialRouter = useSocialRouter();
    const companySettingsRouter = useCompanySettingsRouter();
    const profileSettingsRouter = useProfileSettingsRouter();

    const { applicationParameters, isError } = useApplicationParameters();

    if (isError) {
        // This means you're trying to access an environment that doesn't exist.
        return <GenericError message={t("page_inexistante")} />;
    }
    if (applicationParameters === undefined) {
        return (
            <ThemedApp>
                <Fragment>
                    <CssBaseline />
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "primary.50",
                            zIndex: 9999,
                        }}
                    >
                        <CircularProgress
                            size={40}
                            thickness={4}
                            sx={{
                                color: "primary.main",
                                opacity: 0.7,
                            }}
                        />
                    </Box>
                </Fragment>
            </ThemedApp>
        );
    }

    const currentLanguage = LocalStorage.getItem("language");
    if (currentLanguage !== null) {
        document.documentElement.lang = currentLanguage;
    }

    if (applicationParameters.tabTitle !== undefined && applicationParameters.tabTitle !== "") {
        document.title = `${applicationParameters.tabTitle}`;
    }

    if (applicationParameters.tabIcon !== undefined) {
        const favIcon = document.getElementById("favicon");
        favIcon?.setAttribute("href", applicationParameters.tabIcon);
    }

    const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

    const routerConfig = {
        future: {
            v7_startTransition: true,
            v7_relativeSplatPath: true,
        },
    };

    const appRouter = sentryCreateBrowserRouter(
        createRoutesFromElements(
            <Route path="/" element={<GlobalLayout />} errorElement={<RouteErrorPage canContactSupport={false} />}>
                {authRouter}
                <Route element={<AuthProvider />}>
                    {noCompanyRouter}
                    <Route element={<ProtectedRoute />} errorElement={<RouteErrorPage />}>
                        <Route errorElement={<RouteErrorPage />}>
                            <Route index element={<HomeSwitch />} />
                            {profileSettingsRouter}
                            {companySettingsRouter}
                            <Route path={`${companySlug}/:companyId/${networkSlug}`} element={<NetworkPage />} />
                            {companyRouter}
                            {pagesRouter}
                            {cmsRouter}
                            {settingsRouter}
                            {strategyRouter}
                            {socialRouter}
                            <Route path="documentation" element={<Documentation />} />
                            {/* Catch-all CMS page for non-reserved routes */}
                            <Route path="*" element={<CMSPages />} />
                        </Route>
                    </Route>
                </Route>
            </Route>,
        ),
        routerConfig,
    );

    return (
        <ThemedApp>
            <Fragment>
                <CssBaseline />
                <GlobalStyles
                    styles={{
                        "#launcher-frame": {
                            maxWidth: "175px !important",
                            borderRadius: "28px !important",
                            zIndex: "1190 !important",
                        },
                        'body[data-consent-editor-open="true"] #launcher-frame': {
                            display: "none !important",
                        },
                    }}
                />
                <RouterProvider router={appRouter} />
            </Fragment>
        </ThemedApp>
    );
};

export default Sentry.withProfiler(App);

const AuthProvider = () => {
    const { applicationParameters } = useApplicationParameters();
    const isPdfMode = isAppInPDFMode();
    // Configure plugins based on application parameters
    // Plugins are loaded here to have access to UserContext
    const pluginConfigs: PluginConfig[] = [
        {
            name: "freshdesk",
            enabled: !!applicationParameters?.freshdeskWidgetId,
            loader: () => import("./plugins/freshdesk/FreshdeskPlugin"),
        },
        {
            name: "amplitude",
            enabled: !!applicationParameters?.amplitudeApiKey,
            loader: () => import("./plugins/amplitude/AmplitudePlugin"),
        },
        {
            name: "gtm",
            enabled: !!(applicationParameters?.gtmIdGlobal || applicationParameters?.gtmIdClient),
            loader: () => import("./plugins/gtm/GTMPlugin"),
        },
    ];

    return (
        <DebugProvider>
            <UserProvider>
                {!isPdfMode ?
                    <PluginProvider plugins={pluginConfigs}>
                        <Jimo />
                        <Outlet />
                    </PluginProvider>
                :   <Outlet />}
            </UserProvider>
        </DebugProvider>
    );
};

const ProtectedRoute = () => {
    const sidebarStatus = LocalStorage.getItem("sidebarStatus");

    return (
        <ProtectedContextProvider>
            <BaseTemplate sidebarStatus={sidebarStatus}>
                <Outlet />
            </BaseTemplate>
        </ProtectedContextProvider>
    );
};

const GlobalLayout = () => {
    return (
        <Fragment>
            <ScrollRestoration />
            <Outlet />
        </Fragment>
    );
};
